import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  real,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// D07: FK constraints added. tenantId excluded (partition key in per-tenant DB).
import { people } from "./crm";
import { emailTemplates } from "./email";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Filter tree types (for segments)
// ---------------------------------------------------------------------------

export interface FilterCondition {
  field: string;
  op:
    | "eq"
    | "neq"
    | "gt"
    | "gte"
    | "lt"
    | "lte"
    | "contains"
    | "not_contains"
    | "starts_with"
    | "ends_with"
    | "exists"
    | "not_exists"
    | "in"
    | "not_in"
    | "between";
  value: unknown;
}

export interface FilterGroup {
  operator: "AND" | "OR";
  conditions: Array<FilterCondition | FilterGroup>;
}

export type FilterTree = FilterGroup;

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

export const SEGMENT_TYPES = ["dynamic", "static"] as const;
export type SegmentType = (typeof SEGMENT_TYPES)[number];

export const segments = pgTable(
  "segments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    type: text("type").notNull().default("dynamic"),
    filterTree: jsonb("filter_tree").$type<FilterTree>(),
    cachedCount: integer("cached_count"),
    cachedAt: timestamp("cached_at", tz),
    isSystem: boolean("is_system").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_segments_tenant").on(table.tenantId),
    uniqueIndex("idx_segments_tenant_name").on(table.tenantId, table.name),
  ],
);

// ---------------------------------------------------------------------------
// Custom Field Definitions (per tenant)
// ---------------------------------------------------------------------------

export const CUSTOM_FIELD_TYPES = [
  "text",
  "number",
  "date",
  "boolean",
  "dropdown",
  "url",
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const customFieldDefinitions = pgTable(
  "custom_field_definitions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    label: text("label").notNull(),
    fieldType: text("field_type").notNull(),
    isRequired: boolean("is_required").notNull().default(false),
    options: jsonb("options").$type<string[]>(),
    validation: jsonb("validation").$type<{
      minLength?: number;
      maxLength?: number;
      min?: number;
      max?: number;
      pattern?: string;
    }>(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_custom_field_defs_tenant").on(table.tenantId),
    uniqueIndex("idx_custom_field_defs_tenant_name").on(
      table.tenantId,
      table.name,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Contact Activity Summary (materialized engagement metrics)
// ---------------------------------------------------------------------------

export const contactActivitySummary = pgTable(
  "contact_activity_summary",
  {
    personId: uuid("person_id")
      .primaryKey()
      .references(() => people.id, { onDelete: "cascade" }),
    tenantId: uuid("tenant_id").notNull(),
    emailsSent30d: integer("emails_sent_30d").notNull().default(0),
    emailsOpened30d: integer("emails_opened_30d").notNull().default(0),
    emailsClicked30d: integer("emails_clicked_30d").notNull().default(0),
    lastEmailSentAt: timestamp("last_email_sent_at", tz),
    lastEmailOpenedAt: timestamp("last_email_opened_at", tz),
    lastEmailClickedAt: timestamp("last_email_clicked_at", tz),
    totalRevenue: real("total_revenue").notNull().default(0),
    lastPurchaseAt: timestamp("last_purchase_at", tz),
    engagementScore: real("engagement_score").notNull().default(0),
    engagementStatus: text("engagement_status").notNull().default("new"),
    smsSent30d: integer("sms_sent_30d").default(0).notNull(),
    smsDelivered30d: integer("sms_delivered_30d").default(0).notNull(),
    smsReplied30d: integer("sms_replied_30d").default(0).notNull(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_contact_activity_tenant").on(table.tenantId),
    index("idx_contact_activity_engagement").on(
      table.tenantId,
      table.engagementStatus,
    ),
    index("idx_contact_activity_score").on(
      table.tenantId,
      table.engagementScore,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATUSES = [
  "draft",
  "scheduled",
  "sending",
  "sent",
  "paused",
  "cancelled",
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const CAMPAIGN_TYPES = ["broadcast", "ab_test"] as const;
export type CampaignType = (typeof CAMPAIGN_TYPES)[number];

export interface CampaignSettings {
  fromEmail?: string;
  fromName?: string;
  replyTo?: string;
  subjectOverride?: string;
  failedCount?: number;
  deliveredCount?: number;
  readCount?: number;
}

export const campaigns = pgTable(
  "campaigns",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    type: text("type").notNull().default("broadcast"),
    channel: text("channel").default("email").notNull(),
    name: text("name").notNull(),
    status: text("status").notNull().default("draft"),
    segmentId: uuid("segment_id")
      .references(() => segments.id, { onDelete: "set null" }),
    templateId: uuid("template_id")
      .references(() => emailTemplates.id, { onDelete: "set null" }),
    templateSnapshot: jsonb("template_snapshot"),
    scheduledAt: timestamp("scheduled_at", tz),
    startedAt: timestamp("started_at", tz),
    completedAt: timestamp("completed_at", tz),
    totalRecipients: integer("total_recipients"),
    sentCount: integer("sent_count").notNull().default(0),
    settings: jsonb("settings").$type<CampaignSettings>(),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_campaigns_tenant").on(table.tenantId),
    index("idx_campaigns_tenant_status").on(table.tenantId, table.status),
    index("idx_campaigns_tenant_scheduled").on(
      table.tenantId,
      table.scheduledAt,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Campaign Variants (for A/B testing)
// ---------------------------------------------------------------------------

export const campaignVariants = pgTable(
  "campaign_variants",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    campaignId: uuid("campaign_id")
      .notNull()
      .references(() => campaigns.id, { onDelete: "cascade" }),
    variantLabel: text("variant_label").notNull(),
    percentage: integer("percentage").notNull(),
    templateId: uuid("template_id")
      .references(() => emailTemplates.id, { onDelete: "set null" }),
    subjectOverride: text("subject_override"),
    fromNameOverride: text("from_name_override"),
    sendAtOverride: timestamp("send_at_override", tz),
    sentCount: integer("sent_count").notNull().default(0),
    openCount: integer("open_count").notNull().default(0),
    clickCount: integer("click_count").notNull().default(0),
    conversionCount: integer("conversion_count").notNull().default(0),
    isWinner: boolean("is_winner").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_campaign_variants_campaign").on(table.campaignId),
  ],
);

// ---------------------------------------------------------------------------
// Campaign A/B Config
// ---------------------------------------------------------------------------

export const TEST_VARIABLES = [
  "subject",
  "content",
  "send_time",
  "from_name",
] as const;
export type TestVariable = (typeof TEST_VARIABLES)[number];

export const WINNER_METRICS = [
  "open_rate",
  "click_rate",
  "conversion_rate",
] as const;
export type WinnerMetric = (typeof WINNER_METRICS)[number];

export const campaignAbConfig = pgTable("campaign_ab_config", {
  campaignId: uuid("campaign_id")
    .primaryKey()
    .references(() => campaigns.id, { onDelete: "cascade" }),
  testVariable: text("test_variable").notNull(),
  winnerMetric: text("winner_metric").notNull(),
  winnerWaitHours: integer("winner_wait_hours").notNull().default(4),
  autoSendWinner: boolean("auto_send_winner").notNull().default(true),
  winnerDeterminedAt: timestamp("winner_determined_at", tz),
});

// ---------------------------------------------------------------------------
// Workflow status + re-entry policy constants
// ---------------------------------------------------------------------------

export const WORKFLOW_STATUSES = [
  "draft",
  "active",
  "paused",
  "archived",
] as const;
export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const REENTRY_POLICIES = [
  "once_ever",
  "after_completion",
  "allow_multiple",
] as const;
export type ReentryPolicy = (typeof REENTRY_POLICIES)[number];

// ---------------------------------------------------------------------------
// Workflows
// ---------------------------------------------------------------------------

export interface WorkflowTriggerConfig {
  type: string;
  segmentId?: string;
  eventType?: string;
  config?: Record<string, unknown>;
}

export interface WorkflowQuietHours {
  enabled: boolean;
  start: string;
  end: string;
  timezoneSource: string;
  fallbackTimezone: string;
}

export interface WorkflowFrequencyCap {
  maxEmails: number;
  periodHours: number;
  appliesTo: string;
}

export const workflows = pgTable(
  "workflows",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    status: text("status").notNull().default("draft"),
    currentVersion: integer("current_version"),
    // F4: Multi-trigger support. Array of trigger configs (was single triggerConfig).
    // Migration A.6: UPDATE workflows SET triggers = jsonb_build_array(trigger_config) WHERE trigger_config IS NOT NULL
    triggers: jsonb("triggers").$type<WorkflowTriggerConfig[]>().default([]),
    settings: jsonb("settings").$type<Record<string, unknown>>(),
    reentryPolicy: text("reentry_policy").notNull().default("once_ever"),
    quietHours: jsonb("quiet_hours").$type<WorkflowQuietHours>(),
    frequencyCap: jsonb("frequency_cap").$type<WorkflowFrequencyCap>(),
    folderId: uuid("folder_id"), // A6: optional folder assignment
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_workflows_tenant").on(table.tenantId),
    index("idx_workflows_tenant_status").on(table.tenantId, table.status),
  ],
);

// ---------------------------------------------------------------------------
// Workflow Node & Edge interfaces
// ---------------------------------------------------------------------------

export interface WorkflowNode {
  id: string;
  type: string;
  config: Record<string, unknown>;
  position?: { x: number; y: number };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

// ---------------------------------------------------------------------------
// Workflow Versions
// ---------------------------------------------------------------------------

export const workflowVersions = pgTable(
  "workflow_versions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    workflowId: uuid("workflow_id")
      .notNull()
      .references(() => workflows.id, { onDelete: "cascade" }),
    version: integer("version").notNull(),
    nodes: jsonb("nodes").notNull().$type<WorkflowNode[]>(),
    edges: jsonb("edges").notNull().$type<WorkflowEdge[]>(),
    publishedAt: timestamp("published_at", tz).notNull().defaultNow(),
    publishedBy: text("published_by"),
    changeSummary: text("change_summary"),
  },
  (table) => [
    index("idx_workflow_versions_workflow").on(table.workflowId),
    uniqueIndex("idx_workflow_versions_workflow_version").on(
      table.workflowId,
      table.version,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Enrollment status constants
// ---------------------------------------------------------------------------

export const ENROLLMENT_STATUSES = [
  "active",
  "completed",
  "errored",
  "removed",
] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Workflow Enrollments
// ---------------------------------------------------------------------------

export const workflowEnrollments = pgTable(
  "workflow_enrollments",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    workflowId: uuid("workflow_id")
      .notNull()
      .references(() => workflows.id, { onDelete: "cascade" }),
    workflowVersion: integer("workflow_version").notNull(),
    personId: uuid("person_id")
      .notNull()
      .references(() => people.id, { onDelete: "cascade" }),
    currentNodeId: text("current_node_id"),
    status: text("status").notNull().default("active"),
    enteredAt: timestamp("entered_at", tz).notNull().defaultNow(),
    nextEvaluateAt: timestamp("next_evaluate_at", tz),
    nodeEnteredAt: timestamp("node_entered_at", tz),
    executionContext: jsonb(
      "execution_context",
    ).$type<Record<string, unknown>>(),
    errorCount: integer("error_count").notNull().default(0),
    lastError: text("last_error"),
    completedAt: timestamp("completed_at", tz),
  },
  (table) => [
    index("idx_workflow_enrollments_workflow").on(table.workflowId),
    index("idx_workflow_enrollments_person").on(table.personId),
    index("idx_workflow_enrollments_status_next").on(
      table.tenantId,
      table.status,
      table.nextEvaluateAt,
    ),
    index("idx_workflow_enrollments_workflow_person_status").on(
      table.workflowId,
      table.personId,
      table.status,
    ),
    index("idx_workflow_enrollments_tenant_person").on(
      table.tenantId,
      table.personId,
      table.status,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Workflow Execution Log
// ---------------------------------------------------------------------------

export const workflowExecutionLog = pgTable(
  "workflow_execution_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    enrollmentId: uuid("enrollment_id")
      .notNull()
      .references(() => workflowEnrollments.id, { onDelete: "cascade" }),
    nodeId: text("node_id").notNull(),
    action: text("action").notNull(),
    result: jsonb("result"),
    error: text("error"),
    durationMs: integer("duration_ms"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_workflow_exec_log_enrollment").on(table.enrollmentId),
    index("idx_workflow_exec_log_created").on(table.createdAt),
  ],
);
